/*!
 * Sophie Tea — Real Life Love Stories cursor
 * Neon Glow heart cursor (standalone build).
 *
 * Works on desktop (cursor follows the mouse, hearts trail behind, clicks
 * burst) and touch (deliberate taps burst hearts; scrolling does nothing).
 *
 * Drop into your theme assets and load once:
 *   <script src="heart-cursor.neonglow.js" defer></script>
 *
 * Optional config (set BEFORE this script loads):
 *   <script>
 *     window.__heartCursorConfig = {
 *       color: '#B91C1C',  // hex
 *       size: 0.5,         // 0.3 – 2.0
 *       trail: 1.0,        // 0 = no trail, 3 = dense
 *       burst: 1.0         // click burst intensity
 *     };
 *   </script>
 */
(function () {
  // ───── helpers ─────
  const TAU = Math.PI * 2;
  const lerp = (a, b, t) => a + (b - a) * t;
  const rand = (a, b) => a + Math.random() * (b - a);

  function hexToRgb(hex) {
    const h = hex.replace('#', '');
    return [parseInt(h.substring(0,2),16), parseInt(h.substring(2,4),16), parseInt(h.substring(4,6),16)];
  }
  const rgba = ([r,g,b], a) => `rgba(${r},${g},${b},${a})`;
  const lighten = ([r,g,b], k) => [r+(255-r)*k, g+(255-g)*k, b+(255-b)*k].map(Math.round);

  // pretty heart path centred at (0,0), size = bounding-box half-extent
  function heartPath(ctx, s) {
    ctx.beginPath();
    ctx.moveTo(0, -s * 0.3);
    ctx.bezierCurveTo(-s*0.05, -s*0.85, -s*0.85, -s*0.85, -s*0.85, -s*0.25);
    ctx.bezierCurveTo(-s*0.85,  s*0.15, -s*0.25,  s*0.55,  0,        s*0.95);
    ctx.bezierCurveTo( s*0.25,  s*0.55,  s*0.85,  s*0.15,  s*0.85,  -s*0.25);
    ctx.bezierCurveTo( s*0.85, -s*0.85,  s*0.05, -s*0.85,  0,       -s*0.3);
    ctx.closePath();
  }

  // ───── state ─────
  const state = {
    mouseX: -200, mouseY: -200,
    cx: -200, cy: -200,
    vx: 0, vy: 0,
    px: -200, py: -200,
    speed: 0,
    color: '#FF2E93',
    rgb: hexToRgb('#FF2E93'),
    size: 1,
    trailMul: 1,
    speedMul: 1,
    burstMul: 1,
    hover: 0,
    hoverTarget: 0,
    pressed: false,
    pressedAt: 0,
    particles: [],
    visible: false,
    inited: false,
  };

  let canvas, ctx, dpr = 1;

  function setupCanvas() {
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = innerWidth * dpr;
    canvas.height = innerHeight * dpr;
    canvas.style.width = innerWidth + 'px';
    canvas.style.height = innerHeight + 'px';
    ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  // ───── the neon heart ─────
  function drawNeon(x, y) {
    const t = performance.now();

    // brief, randomly-timed flicker every few seconds
    const slot = Math.floor(t / 70);
    const r = Math.sin(slot * 12.9898) * 43758.5453;
    const flickerRoll = r - Math.floor(r);
    let dim = 1;
    if (flickerRoll > 0.97) dim = 0.55;
    else if (flickerRoll > 0.93) dim = 0.82;
    dim *= 1 + Math.sin(t / 90) * 0.025;     // gentle hum

    const sincePress = state.pressedAt ? t - state.pressedAt : 9e9;
    const surge = Math.max(0, 1 - sincePress / 320);
    const intensity = Math.max(0.2, dim) * (1 + state.hover * 0.35 + surge * 0.6);
    const baseSize = (18 + state.hover * 4) * state.size;

    ctx.save();
    ctx.translate(x, y);

    const drawTube = (lineWidth, color, blur, alpha) => {
      ctx.save();
      heartPath(ctx, baseSize);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineWidth = lineWidth;
      ctx.strokeStyle = color;
      ctx.globalAlpha = alpha;
      ctx.shadowColor = color;
      ctx.shadowBlur = blur;
      ctx.stroke();
      ctx.restore();
    };

    // outer bloom
    drawTube(2, rgba(state.rgb, 1), 40 * intensity, 0.80 * intensity);
    drawTube(2, rgba(state.rgb, 1), 26 * intensity, 0.85 * intensity);
    // mid glow — only slightly lifted so deep reds stay deep
    drawTube(4, rgba(lighten(state.rgb, 0.12), 1), 18 * intensity, 0.95 * intensity);
    // tube core — lifted but not near-white
    drawTube(2.2, rgba(lighten(state.rgb, 0.45), 1), 8 * intensity, Math.min(1, intensity));
    // bright centerline — tinted, NOT pure white, so deep reds don't pink-out
    drawTube(0.8, rgba(lighten(state.rgb, 0.85), 1), 4 * intensity, Math.min(1, intensity));

    ctx.restore();
  }

  // ───── particles ─────
  function spawnTrailHeart() {
    state.particles.push({
      kind: 'neonheart',
      x: state.cx + rand(-4, 4),
      y: state.cy + rand(-4, 4),
      vx: rand(-0.3, 0.3),
      vy: rand(-0.9, -0.2),
      size: rand(5, 9) * state.size,
      rot: rand(-0.3, 0.3),
      vrot: rand(-0.03, 0.03),
      life: 0,
      maxLife: rand(45, 75),
      gravity: 0,
    });
  }

  function clickBurst(x, y) {
    const b = state.burstMul;
    const n = Math.round(10 * b);
    for (let i = 0; i < n; i++) {
      const a = (i / n) * TAU + rand(-0.12, 0.12);
      const speed = rand(0.9, 2.0) * b;
      state.particles.push({
        kind: 'neonheart',
        x, y,
        vx: Math.cos(a) * speed,
        vy: Math.sin(a) * speed,
        size: rand(5, 10) * state.size,
        rot: rand(-0.18, 0.18),
        vrot: rand(-0.012, 0.012),
        life: 0,
        maxLife: rand(80, 140),
        gravity: 0,
      });
    }
    // shockwave ring
    state.particles.push({
      kind: 'ring',
      x, y,
      size: 0,
      maxSize: 60 * b * state.size,
      life: 0,
      maxLife: 28,
    });
  }

  function renderParticleNeonHeart(p) {
    // Render the particle using the EXACT same 5-pass layering as drawNeon,
    // so trail + burst hearts look identical (just smaller and fading).
    p.x += p.vx;
    p.y += p.vy;
    p.vy += p.gravity || 0;
    p.vx *= 0.995;
    p.rot += p.vrot;

    const a = Math.max(0, 1 - p.life / p.maxLife);

    // Temporarily override state so drawNeon at (0,0) renders at this particle's size
    const saveSize = state.size, saveHover = state.hover, savePressedAt = state.pressedAt;
    state.size = p.size / 18;
    state.hover = 0;
    state.pressedAt = 0;

    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate(p.rot);
    ctx.globalAlpha = a;
    drawNeon(0, 0);
    ctx.restore();

    state.size = saveSize;
    state.hover = saveHover;
    state.pressedAt = savePressedAt;
  }

  function renderParticleRing(p) {
    const t = p.life / p.maxLife;
    const a = Math.max(0, 1 - t);
    const r = p.maxSize * t;
    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, TAU);
    ctx.strokeStyle = rgba(state.rgb, a * 0.65);
    ctx.lineWidth = 2;
    ctx.shadowColor = rgba(state.rgb, a);
    ctx.shadowBlur = 12;
    ctx.stroke();
    ctx.restore();
  }

  function updateAndDrawParticles() {
    for (let i = state.particles.length - 1; i >= 0; i--) {
      const p = state.particles[i];
      p.life += 1;
      if (p.kind === 'neonheart') renderParticleNeonHeart(p);
      else if (p.kind === 'ring') renderParticleRing(p);
      if (p.life >= p.maxLife) state.particles.splice(i, 1);
    }
  }

  // ───── main loop ─────
  function frame() {
    const ease = Math.min(0.95, 0.3 * state.speedMul);
    state.px = state.cx; state.py = state.cy;
    state.cx = lerp(state.cx, state.mouseX, ease);
    state.cy = lerp(state.cy, state.mouseY, ease);
    state.vx = state.cx - state.px;
    state.vy = state.cy - state.py;
    state.speed = Math.hypot(state.vx, state.vy);
    state.hover = lerp(state.hover, state.hoverTarget, 0.18);

    ctx.clearRect(0, 0, innerWidth, innerHeight);

    if (state.speed > 0.4 && state.trailMul > 0) {
      const density = state.trailMul * state.speedMul;
      if (Math.random() < 0.22 * density) spawnTrailHeart();
    }

    updateAndDrawParticles();
    if (state.visible) drawNeon(state.cx, state.cy);

    requestAnimationFrame(frame);
  }

  // ───── input ─────
  // We treat touch and mouse differently:
  //   Mouse: cursor follows the pointer continuously; click bursts.
  //   Touch: cursor stays hidden; ONLY a deliberate tap (short press,
  //          minimal movement) triggers a burst. Scrolling never bursts.
  let touch = null;
  const TAP_MAX_MS = 350;
  const TAP_MAX_MOVE = 12;   // pixels

  function onMove(e) {
    // Ignore touch-derived pointermove — those are scroll gestures.
    if (e.pointerType === 'touch') return;
    const t = e;
    state.mouseX = t.clientX;
    state.mouseY = t.clientY;
    if (!state.visible) {
      state.visible = true;
      state.cx = state.mouseX;
      state.cy = state.mouseY;
    }
    const el = document.elementFromPoint(state.mouseX, state.mouseY);
    state.hoverTarget = el && el.closest('a, button, input, textarea, [role=button], [data-hover]') ? 1 : 0;
  }
  function onLeave() {
    state.visible = false;
    state.hoverTarget = 0;
  }
  function onMouseDown(e) {
    if (e.pointerType === 'touch') return;
    state.pressed = true;
    state.pressedAt = performance.now();
    clickBurst(e.clientX, e.clientY);
  }
  function onMouseUp(e) {
    if (e.pointerType === 'touch') return;
    state.pressed = false;
  }

  function onTouchStart(e) {
    const t = e.touches[0];
    touch = {
      startX: t.clientX,
      startY: t.clientY,
      startT: performance.now(),
      maxMove: 0,
    };
  }
  function onTouchMove(e) {
    if (!touch) return;
    const t = e.touches[0];
    const dx = t.clientX - touch.startX;
    const dy = t.clientY - touch.startY;
    const d = Math.hypot(dx, dy);
    if (d > touch.maxMove) touch.maxMove = d;
  }
  function onTouchEnd(e) {
    if (!touch) return;
    const dur = performance.now() - touch.startT;
    const isTap = dur < TAP_MAX_MS && touch.maxMove < TAP_MAX_MOVE;
    if (isTap) {
      // burst at the original touch position
      clickBurst(touch.startX, touch.startY);
    }
    touch = null;
  }

  // ───── init ─────
  function init(cfg) {
    if (state.inited) return;
    state.inited = true;
    cfg = cfg || {};
    state.color = cfg.color || state.color;
    state.rgb = hexToRgb(state.color);
    state.size = cfg.size ?? 1;
    state.trailMul = cfg.trail ?? 1;
    state.speedMul = cfg.speed ?? 1;
    state.burstMul = cfg.burst ?? 1;

    // Hide native cursor on non-touch (so the neon heart is THE cursor)
    if (!window.matchMedia || !window.matchMedia('(pointer: coarse)').matches) {
      const s = document.createElement('style');
      s.id = '__heart_cursor_hide__';
      s.textContent = 'html,body{cursor:none!important}a,button,input,textarea,[role=button]{cursor:none!important}';
      document.head.appendChild(s);
    }

    canvas = document.createElement('canvas');
    canvas.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:2147483641';
    document.body.appendChild(canvas);
    setupCanvas();

    window.addEventListener('resize', setupCanvas);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerleave', onLeave);
    window.addEventListener('mouseleave', onLeave);
    window.addEventListener('pointerdown', onMouseDown);
    window.addEventListener('pointerup', onMouseUp);
    // Touch: detect real taps (short + no movement), ignore scrolls
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('touchcancel', () => { touch = null; }, { passive: true });

    requestAnimationFrame(frame);
  }

  // ───── auto-start ─────
  function autostart() {
    const cfg = window.__heartCursorConfig || {};
    init({
      color: cfg.color || '#B91C1C',  // deep red
      size:  cfg.size  || 0.5,         // smaller default
      trail: cfg.trail || 1,
      speed: cfg.speed || 1,
      burst: cfg.burst || 1,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autostart);
  } else {
    autostart();
  }
})();